<template>
    <ValidationObserver ref="institutionalWithdrawalForm">
        <b-container class="mt-5">
            <b-card class="p-5">
                <b-row class="text-center align-items-center mb-4">
                    <b-col
                        cols="12"
                        class="d-flex align-items-center justify-content-between"
                    >
                        <img
                            src="../../../assets/img/BAU_Logo.png"
                            height="75"
                            alt="BAU Logo"
                        />
                        <h6
                            class="mb-0"
                            v-html="$t('bau_inspiration_text')"
                        ></h6>
                    </b-col>
                </b-row>
                <div class="mb-4">
                    <h4 class="text-center font-weight-bold">
                        {{ $t("institutional_withdrawal_request") }}
                    </h4>
                    <p v-html="$t('institutional_withdrawal_instructions')"></p>
                </div>

                <!-- Student Information -->
                <b-row class="mb-3">
                    <b-col cols="12">
                        <h5 class="font-weight-bold">
                            {{ $t("student_information") }}
                        </h5>
                        <hr class="yellow-line" />
                    </b-col>
                    <b-col cols="12">
                        <ValidationProvider name="student_name" rules="required" v-slot="{ valid, errors }">
                            <b-form-group :label="$t('student_name')">
                                <b-form-input v-model="form.student_name" :state="errors[0] ? false : valid ? true : null" />
                                <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]" />
                            </b-form-group>
                        </ValidationProvider>
                    </b-col>
                    <b-col cols="12" md="4">
                        <ValidationProvider name="phone" rules="required" v-slot="{ valid, errors }">
                            <b-form-group :label="$t('phone')">
                                <b-form-input v-model="form.phone" :state="errors[0] ? false : valid ? true : null" />
                                <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]" />
                            </b-form-group>
                        </ValidationProvider>
                    </b-col>
                    <b-col cols="12" md="4">
                        <ValidationProvider name="email" rules="required|email" v-slot="{ valid, errors }">
                            <b-form-group :label="$t('email')">
                                <b-form-input v-model="form.email" :state="errors[0] ? false : valid ? true : null" />
                                <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]" />
                            </b-form-group>
                        </ValidationProvider>
                    </b-col>
                    <b-col cols="12" md="4">
                        <ValidationProvider name="student_id" rules="required" v-slot="{ valid, errors }">
                            <b-form-group :label="$t('student_id')">
                                <b-form-input v-model="form.student_id" :state="errors[0] ? false : valid ? true : null" />
                                <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]" />
                            </b-form-group>
                        </ValidationProvider>
                    </b-col>
                </b-row>

                <!-- Program and Reason -->
                <b-row class="mb-3">
                    <b-col cols="12">
                        <hr class="yellow-line" />
                        <h5 class="font-weight-bold">
                            {{ $t("program_of_study") }}
                        </h5>
                        <b-form-input v-model="form.program_of_study" />
                        <hr class="yellow-line" />
                    </b-col>
                    <b-col cols="12">
                        <ValidationProvider name="reason_for_withdrawal" rules="" v-slot="{ valid, errors }">
                            <b-form-group :label="$t('reason_for_withdrawal')">
                                <b-form-checkbox-group
                                    v-model="form.withdrawal_reasons"
                                    :options="withdrawalOptions"
                                    :state="errors[0] ? false : (valid ? true : null)"
                                    stacked
                                ></b-form-checkbox-group>
                                <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"></b-form-invalid-feedback>
                            </b-form-group>
                        </ValidationProvider>
                        <hr class="yellow-line" />
                    </b-col>
                </b-row>

                <!-- Student Confirmation -->
                <b-row class="mt-4">
                    <b-col cols="12">
                        <p v-html="$t('withdrawal_policy_acknowledgement') "></p>
                    </b-col>
                    <b-col cols="12" md="6">
                        <ValidationProvider name="student_signature" rules="required" v-slot="{ valid, errors }">
                            <b-form-group :label="$t('student_signature')">
                                <b-form-input v-model="form.student_signature" :state="errors[0] ? false : valid ? true : null" />
                                <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]" />
                            </b-form-group>
                        </ValidationProvider>
                    </b-col>
                    <b-col cols="12" md="6">
                        <ValidationProvider name="signature_date" rules="required" v-slot="{ valid, errors }">
                            <b-form-group :label="$t('date')">
                                <select-date v-model="form.signature_date" :validation-error="errors[0]" />
                            </b-form-group>
                        </ValidationProvider>
                    </b-col>
                </b-row>

                <hr class="yellow-line" />

                <!-- Official Use Only -->
                <b-row class="mt-4">
                    <b-col cols="12">
                        <h5 class="font-weight-bold text-uppercase">
                            {{ $t("official_use_only") }}
                        </h5>
                    </b-col>
                    <b-col cols="12" md="6">
                        <ValidationProvider name="date_received" rules="required" v-slot="{ valid, errors }">
                            <b-form-group :label="$t('date_received')">
                                <select-date v-model="form.date_received" :validation-error="errors[0]" />
                            </b-form-group>
                        </ValidationProvider>
                    </b-col>
                    <b-col cols="12" md="6">
                        <ValidationProvider name="date_processed" rules="required" v-slot="{ valid, errors }">
                            <b-form-group :label="$t('date_processed')">
                                <select-date v-model="form.date_processed" :validation-error="errors[0]" />
                            </b-form-group>
                        </ValidationProvider>
                    </b-col>
                    <b-col cols="12" md="6">
                        <ValidationProvider name="registrar_signature" rules="required" v-slot="{ valid, errors }">
                            <b-form-group :label="$t('registrar_signature')">
                                <b-form-input v-model="form.registrar_signature" :state="errors[0] ? false : valid ? true : null" />
                                <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]" />
                            </b-form-group>
                        </ValidationProvider>
                    </b-col>
                </b-row>
            </b-card>
        </b-container>
    </ValidationObserver>
</template>

<script>
import SelectDate from "@/components/interactive-fields/SelectDate.vue";

export default {
    components: { SelectDate },
    data() {
        return {
            form: {
                student_name: null,
                phone: null,
                email: null,
                student_id: null,
                program_of_study: null,
                withdrawal_reasons: [],
                other_reason: null,
                student_signature: null,
                signature_date: null,
                date_received: null,
                date_processed: null,
                registrar_signature: null,
            },
            withdrawalOptions: [
                { value: 'workload', text: 'Workload' },
                { value: 'illness', text: 'Illness' },
                { value: 'financial', text: 'Financial Difficulties' },
                { value: 'transfer', text: 'Transferring to another institution (Please indicate the School’s Name)' },
                { value: 'other', text: 'Other (Please Explain)' },
            ],
        };
    },
    metaInfo() {
        return {
            title: this.$t("institutional_withdrawal_request"),
        };
    },
};
</script>

<style scoped>
.yellow-line {
    border-top: 2px solid #ffc107;
    margin: 1rem 0;
}
</style>
